import React from 'react';

// SVG Icons
const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
  </svg>
);

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

/**
 * ValidationMessage - Displays validation results from API
 * Shows errors, warnings, and suggestions in a structured format
 */
const ValidationMessage = ({
  validation,
  showSuccess = false,
  compact = false,
}) => {
  if (!validation) return null;

  const { valid, errors = [], warnings = [], suggestions = [] } = validation;

  // If valid and no warnings/suggestions, optionally show success
  if (valid && errors.length === 0 && warnings.length === 0) {
    if (showSuccess) {
      return (
        <div className="alert alert-success mb-4">
          <SuccessIcon />
          <div>
            <h3 className="font-bold">Validation Passed</h3>
            <div className="text-sm">Strategy configuration is valid and ready to save.</div>
          </div>
        </div>
      );
    }
    return null;
  }

  // Compact mode - single alert with all messages
  if (compact) {
    const alertClass = errors.length > 0 ? 'alert-error' : warnings.length > 0 ? 'alert-warning' : 'alert-info';
    const allMessages = [...errors, ...warnings, ...suggestions];
    
    return (
      <div className={`alert ${alertClass} mb-4`}>
        <div>
          {allMessages.map((msg, index) => (
            <p key={index} className="text-sm">• {msg}</p>
          ))}
        </div>
      </div>
    );
  }

  // Full mode - separate sections for each type
  return (
    <div className="space-y-2 mb-4">
      {/* Errors */}
      {errors.length > 0 && (
        <div className="alert alert-error">
          <div>
            <h3 className="font-bold flex items-center gap-2">
              <ErrorIcon /> Errors
            </h3>
            <ul className="list-none mt-2 space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="text-sm flex items-start gap-2">
                  <span className="text-error">•</span>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="alert alert-warning">
          <div>
            <h3 className="font-bold flex items-center gap-2">
              <WarningIcon /> Warnings
            </h3>
            <ul className="list-none mt-2 space-y-1">
              {warnings.map((warning, index) => (
                <li key={index} className="text-sm flex items-start gap-2">
                  <span className="text-warning">•</span>
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="alert alert-info">
          <div>
            <h3 className="font-bold flex items-center gap-2">
              <InfoIcon /> Suggestions
            </h3>
            <ul className="list-none mt-2 space-y-1">
              {suggestions.map((suggestion, index) => (
                <li key={index} className="text-sm flex items-start gap-2">
                  <span className="text-info">•</span>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationMessage;
